import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const { user, profile, signOut, isAdmin } = useAuth();

  const servicesTimeoutRef = useState<NodeJS.Timeout | null>(null);

  // Check if current page has a dark hero background
  const hasDarkHero = location.pathname === '/' || location.pathname === '/rituals' || location.pathname === '/corporate-ceremonies';
  const showWhiteText = hasDarkHero && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowServicesDropdown(false);
    setShowUserDropdown(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const serviceLines = [
    {
      id: 'ritual-services',
      title: 'Ritual Services',
      icon: 'ri-ancient-gate-line',
      description: 'Traditional pujas performed with dignity',
      subServices: [
        { name: 'All Rituals', path: '/rituals', highlight: true },
        { name: 'Daily Puja & Archana', path: '/ritual-category/daily-puja' },
        { name: 'Special Purpose Puja', path: '/ritual-category/special-purpose' },
        { name: 'Life Event Rituals', path: '/ritual-category/life-events' },
        { name: 'Shradh & Pitru Paksha', path: '/ritual-category/shradh' }
      ]
    },
    {
      id: 'festival-services',
      title: 'Festival & Occasion Services',
      icon: 'ri-calendar-event-line',
      description: 'Celebrate festivals with tradition',
      subServices: [
        { name: 'All Festivals', path: '/festivals', highlight: true },
        { name: 'Diwali Puja', path: '/festival/diwali' },
        { name: 'Navratri Rituals', path: '/festival/navaratri' },
        { name: 'Ganesh Chaturthi', path: '/festival/ganesh-chaturthi' },
        { name: 'Satyanarayan Katha', path: '/ritual-category/satyanarayan' }
      ]
    },
    {
      id: 'potlis-subscriptions',
      title: 'Festival Potlis & Subscriptions',
      icon: 'ri-gift-line',
      description: 'Puja samagri delivered to your doorstep',
      subServices: [
        { name: 'Festival Puja Potlis', path: '/festival-potlis', highlight: true },
        { name: 'Annual Subscription', path: '/annual-subscription' },
        { name: 'International Shipping', path: '/international-shipping' }
      ]
    },
    {
      id: 'temple-services',
      title: 'Pooja at Temple',
      icon: 'ri-home-heart-line',
      description: 'Sacred rituals at India\'s top temples',
      subServices: [
        { name: 'All Sacred Temples', path: '/temples', highlight: true },
        { name: 'Kashi Vishwanath', path: '/temples#kashi-vishwanath' },
        { name: 'Tirupati Balaji', path: '/temples#tirupati-balaji' },
        { name: 'Kedarnath', path: '/temples#kedarnath' },
        { name: 'Vaishno Devi', path: '/temples#vaishno-devi' }
      ]
    },
    {
      id: 'spiritual-merchandise',
      title: 'Spiritual Merchandise',
      icon: 'ri-store-2-line',
      description: 'Authentic puja items & sacred products',
      subServices: [
        { name: 'All Products', path: '/spiritual-merchandise', highlight: true },
        { name: 'Puja Items & Samagri', path: '/spiritual-merchandise#puja-items' },
        { name: 'Deity Idols & Murtis', path: '/spiritual-merchandise#deity-idols' },
        { name: 'Rudraksha & Malas', path: '/spiritual-merchandise#rudraksha-malas' },
        { name: 'Home Temple & Mandirs', path: '/spiritual-merchandise#home-temple' }
      ]
    },
    {
      id: 'online-classes',
      title: 'Online Classes & Content',
      icon: 'ri-graduation-cap-line',
      description: 'Learn from authentic sources',
      subServices: [
        { name: 'All Courses', path: '/online-classes', highlight: true },
        { name: 'Vedic Chanting & Mantras', path: '/online-classes#vedic-chanting' },
        { name: 'Puja Vidhi & Rituals', path: '/online-classes#puja-vidhi' },
        { name: 'Bhagavad Gita Study', path: '/online-classes#bhagavad-gita' },
        { name: 'Sanskrit for Devotees', path: '/online-classes#sanskrit-basics' }
      ]
    },
    {
      id: 'senior-services',
      title: 'Senior Citizen Services',
      icon: 'ri-heart-2-line',
      description: 'Compassionate care for elders',
      subServices: [
        { name: 'All Senior Services', path: '/senior-citizen-services', highlight: true },
        { name: 'Daily Puja Assistance', path: '/senior-citizen-services#daily-puja-assistance' },
        { name: 'Spiritual Companionship', path: '/senior-citizen-services#spiritual-companionship' },
        { name: 'Pilgrimage Assistance', path: '/senior-citizen-services#pilgrimage-assistance' },
        { name: 'Home Ritual Services', path: '/senior-citizen-services#home-ritual-services' }
      ]
    },
    {
      id: 'corporate-services',
      title: 'Corporate / Office Ceremonies',
      icon: 'ri-building-line',
      description: 'Professional ceremonial services',
      subServices: [
        { name: 'All Corporate Ceremonies', path: '/corporate-ceremonies', highlight: true },
        { name: 'Office Inauguration Pooja', path: '/ritual-category/corporate-inauguration' },
        { name: 'Workspace Blessing', path: '/ritual-category/corporate-blessing' },
        { name: 'Festival Ceremonies for Offices', path: '/ritual-category/corporate-festivals' },
        { name: 'Annual Traditional Observances', path: '/ritual-category/corporate-annual' }
      ]
    },
    {
      id: 'donations-seva',
      title: 'Donations & Seva',
      icon: 'ri-hand-heart-line',
      description: 'Support temples and feed the needy',
      subServices: [
        { name: 'All Donations', path: '/donations', highlight: true },
        { name: 'Temple Support', path: '/donations#temple' },
        { name: 'Annadan Seva', path: '/donations#annadan' }
      ]
    }
  ];

  const handleServicesMouseEnter = () => {
    if (servicesTimeoutRef[0]) {
      clearTimeout(servicesTimeoutRef[0]);
    }
    setShowServicesDropdown(true);
  };

  const handleServicesMouseLeave = () => {
    const timeout = setTimeout(() => {
      setShowServicesDropdown(false);
    }, 150);
    servicesTimeoutRef[1](timeout);
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserDropdown(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : hasDarkHero ? 'bg-transparent' : 'bg-white shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img src="https://public.readdy.ai/ai/img_res/15313995-b9c8-4137-afe3-659f58e4dd9d.png" alt="EkDharma Logo" className="h-10 w-10 sm:h-12 sm:w-12 object-contain" />
            <span className={`text-xl sm:text-2xl font-light tracking-wide ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}>EkDharma</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className={`text-base hover:text-[#C78A2B] transition-colors cursor-pointer ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}
            >
              Home
            </Link>
            
            {/* Services Mega Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <button 
                className={`text-base hover:text-[#C78A2B] transition-colors cursor-pointer flex items-center gap-1 ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}
              >
                Services
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${showServicesDropdown ? 'rotate-180' : ''}`}></i>
              </button>
              
              {showServicesDropdown && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-1 w-[800px]">
                  <div className="bg-white rounded-xl shadow-2xl py-6 px-6 border border-gray-100">
                    <div className="grid grid-cols-3 gap-4">
                      {serviceLines.map((service) => (
                        <div key={service.id} className="p-3 rounded-lg hover:bg-[#FAF7F2] transition-colors">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-8 h-8 rounded-full bg-[#C78A2B]/10 flex items-center justify-center">
                              <i className={`${service.icon} text-sm text-[#C78A2B]`}></i>
                            </div>
                            <div>
                              <h4 className="text-xs font-semibold text-[#2E2E2E]">{service.title}</h4>
                            </div>
                          </div>
                          <div className="space-y-0.5 ml-10">
                            {service.subServices.map((sub, idx) => (
                              <Link
                                key={idx}
                                to={sub.path}
                                className={`block px-2 py-1 text-xs rounded-md transition-colors cursor-pointer ${sub.highlight ? 'text-[#C78A2B] font-semibold hover:bg-[#C78A2B]/10' : 'text-gray-600 hover:text-[#C78A2B] hover:bg-white'}`}
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA at bottom */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <Link
                        to="/services"
                        className="flex items-center justify-center gap-2 w-full py-3 bg-[#C78A2B] text-white rounded-lg text-sm font-medium hover:bg-[#B8860B] transition-colors cursor-pointer"
                      >
                        <i className="ri-calendar-check-line"></i>
                        View All Services & Book Now
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/priests" 
              className={`text-base hover:text-[#C78A2B] transition-colors cursor-pointer ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}
            >
              Our Priests
            </Link>

            <Link 
              to="/for-nris" 
              className={`text-base hover:text-[#C78A2B] transition-colors cursor-pointer ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}
            >
              For NRIs
            </Link>
            
            <Link 
              to="/about" 
              className={`text-base hover:text-[#C78A2B] transition-colors cursor-pointer ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}
            >
              About
            </Link>

            {/* User Account Dropdown */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${showWhiteText ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#C78A2B] flex items-center justify-center">
                    <i className="ri-user-line text-white text-sm"></i>
                  </div>
                  <span className={`text-sm ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}>{profile?.full_name?.split(' ')[0] || 'Account'}</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform ${showWhiteText ? 'text-white' : ''} ${showUserDropdown ? 'rotate-180' : ''}`}></i>
                </button>

                {showUserDropdown && (
                  <div className="absolute top-full right-0 pt-1 w-48">
                    <div className="bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                      <Link
                        to="/my-bookings"
                        className="block px-4 py-2.5 text-sm text-[#2E2E2E] hover:bg-[#FAF7F2] hover:text-[#C78A2B] transition-colors cursor-pointer"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <i className="ri-calendar-check-line mr-2"></i>
                        My Bookings
                      </Link>
                      {isAdmin && (
                        <Link
                          to="/admin/dashboard"
                          className="block px-4 py-2.5 text-sm text-[#2E2E2E] hover:bg-[#FAF7F2] hover:text-[#C78A2B] transition-colors cursor-pointer"
                          onClick={() => setShowUserDropdown(false)}
                        >
                          <i className="ri-dashboard-line mr-2"></i>
                          Admin Dashboard
                        </Link>
                      )}
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={handleSignOut}
                        className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-logout-box-line mr-2"></i>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to="/auth/login" 
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${showWhiteText ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <i className={`ri-user-line text-lg ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}></i>
                <span className={`text-sm ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}>Sign In</span>
              </Link>
            )}
            
            <Link 
              to="/contact" 
              className={`px-6 py-2.5 border-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer font-medium ${showWhiteText ? 'border-white text-white hover:bg-white hover:text-[#2E2E2E]' : 'border-[#C78A2B] text-[#C78A2B] hover:bg-[#C78A2B] hover:text-white'}`}
            >
              Talk to a Priest
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${showWhiteText ? 'text-white' : 'text-[#2E2E2E]'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4 bg-white rounded-lg">
            <div className="flex flex-col gap-4">
              <Link 
                to="/" 
                className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
              >
                Home
              </Link>
              
              {/* Mobile Services Dropdown */}
              <div>
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="w-full flex items-center justify-between text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
                >
                  <span>Services</span>
                  <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileServicesOpen && (
                  <div className="mt-3 ml-2 flex flex-col gap-4">
                    {serviceLines.map((service) => (
                      <div key={service.id}>
                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                          <i className={`${service.icon} text-[#C78A2B]`}></i>
                          {service.title}
                        </h4>
                        <div className="ml-2 flex flex-col gap-2">
                          {service.subServices.map((sub, idx) => (
                            <Link
                              key={idx}
                              to={sub.path}
                              className={`text-sm transition-colors cursor-pointer ${sub.highlight ? 'text-[#C78A2B] font-semibold' : 'text-[#2E2E2E] hover:text-[#C78A2B]'}`}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link 
                to="/priests" 
                className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
              >
                Our Priests
              </Link>

              <Link 
                to="/for-nris" 
                className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
              >
                For NRIs
              </Link>
              
              <Link 
                to="/about" 
                className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
              >
                About
              </Link>

              {/* Mobile User Account */}
              {user ? (
                <>
                  <Link 
                    to="/my-bookings" 
                    className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    My Bookings
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin/dashboard" 
                      className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
                    >
                      <i className="ri-dashboard-line mr-2"></i>
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="text-left text-base text-red-600 hover:text-red-700 transition-colors cursor-pointer"
                  >
                    <i className="ri-logout-box-line mr-2"></i>
                    Sign Out
                  </button>
                </>
              ) : (
                <Link 
                  to="/auth/login" 
                  className="text-base text-[#2E2E2E] hover:text-[#C78A2B] transition-colors cursor-pointer"
                >
                  <i className="ri-user-line mr-2"></i>
                  Sign In
                </Link>
              )}
              
              <Link 
                to="/contact" 
                className="px-6 py-2.5 border-2 border-[#C78A2B] text-[#C78A2B] rounded-lg hover:bg-[#C78A2B] hover:text-white transition-colors whitespace-nowrap cursor-pointer font-medium text-center"
              >
                Talk to a Priest
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
